/**
 * Metric view screen chart — Alpine.js component.
 *
 * Owns the Overview card's chart: first draw, client-side chart-type switching, and
 * teardown. Two renderers share the card, split by MetricChartType::supersetViz():
 *
 *  - the shapes Superset has a chart for (line, column, horizontal bar, pie, donut,
 *    gauge) draw through <x-charts.superset-chart>, the dashboards' own chart code, from
 *    the contracts the payload carries for them (MetricChartContractBuilder), with the
 *    app's tooltip passed through the host's tooltip hook;
 *  - the rest draw with the app's own echarts, through charts/chart-options.js.
 *
 * The echarts instance lifecycle — the zero-width container wait, instance reuse, the
 * devicePixelRatio rebuild and teardown — lives in charts/echarts-lifecycle.js, shared
 * with the site analytics report. Every branch of it is a bug someone already paid for,
 * so there is exactly one copy.
 *
 * Prefix: msc_
 */

import { createEchartsLifecycle, memoisedImport } from '../charts/echarts-lifecycle';
import { TOOLTIP_CHROME, supersetSliceTooltipFormatter, supersetTooltipFormatter } from '../charts/metric-tooltip';

/**
 * echarts is only needed once a metric page is open, but this module is reachable
 * from app.js — the single Vite entry on every layout — so importing it statically
 * would ship the whole charting payload on the login screen.
 *
 * The namespace, not the default export: the caller also needs REGISTERED_SERIES.
 */
const loadECharts = memoisedImport(() => import('../charts/echarts-metric-chart'));

const CHART_DATA_EVENT = 'superset-chart-data';

/**
 * Every tooltip on this screen is appended to <body>, whichever renderer draws the chart: the
 * Overview card is overflow-hidden, so a tooltip placed inside the chart is cut off at the card's
 * edge exactly where the right-hand periods are hovered (chart-options.js's SCREEN_TOOLTIP_CHROME).
 */
const HOST_TOOLTIP_CHROME = { ...TOOLTIP_CHROME, appendToBody: true };

/**
 * A slice's share of the drawn whole, to one decimal, as this screen has always printed it. The
 * pie's tooltip and the host's sr-only table both print shares through this one function, so the
 * two cannot disagree about a slice.
 */
const formatShare = (value, total) => (total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '');

/**
 * A point the pie draws a wedge for: a reading above zero, as MetricChartContractBuilder::pie()
 * builds its slices. A padding period or a measured zero is no part of a whole.
 */
const drawsASlice = (point) => typeof point?.value === 'number' && Number.isFinite(point.value) && point.value > 0;

/**
 * @param {?object} initialPayload  MetricChartPayloadBuilder's output.
 * @param {object}  text            Translated strings, passed in from Blade. Every string
 *                                  this component announces has a visible twin rendered by
 *                                  __() in chart.blade.php; hardcoding them here would leave
 *                                  screen-reader and localised users with English while the
 *                                  page around them translated.
 */
export default (initialPayload = null, text = {}) => {
    // Kept in the factory closure, not as reactive properties: Alpine deep-proxies every
    // property, and proxying a live echarts instance (its series models, coord systems,
    // the zrender tree) is needless hot-path overhead and risks breaking echarts'
    // internal identity checks. chartLoadFailed MUST stay reactive — x-show reads it.
    let lifecycle = null;
    // Whether the host holds a chart this component sent it, so switching to a shape the app's
    // echarts draws takes that chart down once rather than on every render.
    let hostHolds = false;

    return {
        payload: initialPayload,
        selectedType: initialPayload?.selectedType ?? null,
        chartLoadFailed: false,
        /**
         * Whether the SUPERSET HOST's own draw failed, as opposed to the app's echarts.
         *
         * A separate flag rather than reusing chartLoadFailed, because the two have different
         * owners and different boxes: chartLoadFailed swaps in this component's load-failed
         * message, while a host failure is drawn by the host inside its own box, with its own
         * sentence and its own Retry button. This component must not render a second failure
         * message over it — all it needs to know is that there is no chart on screen, so the
         * override footnote must not claim one was substituted.
         *
         * Reactive: msc_fallbackMessage reads it.
         */
        hostLoadFailed: false,
        // The type the canvas last actually drew — may differ from selectedType when a
        // series is not registered. Reactive: the accessibility getters read it.
        drawnType: null,
        // Which renderer draws the chart now: 'host' (the Superset bundle), 'echarts' (the app's
        // own) or null (nothing drawn). Reactive: the Blade shows one renderer's box by it.
        drawnBy: null,
        chartRenderToken: 0,
        msc_text: {
            noData: text.noData ?? 'No data to display for the current metric in the current interval',
            loadFailed: text.loadFailed ?? 'The chart could not be loaded. Please refresh the page.',
            chartType: text.chartType ?? 'Chart type: :type',
            fallback: text.fallback ?? 'Showing a different chart: :reason.',
            metricName: text.metricName ?? '',
            ariaLabel: text.ariaLabel ?? ':type chart of :name, :count periods. The same data follows as a table.',
            ariaLabelSingle: text.ariaLabelSingle ?? ':type chart of :name, showing one summary value. The same data follows as a table.',
            caption: text.caption ?? ':type chart data for :name',
            dataCaption: text.dataCaption ?? 'Data for :name',
            inProgress: text.inProgress ?? 'in progress',
            noDataCell: text.noDataCell ?? 'No data',
            latest: text.latest ?? 'Latest',
            period: text.period ?? 'Period',
            thisPeriod: text.thisPeriod ?? 'This period',
            noDataPeriod: text.noDataPeriod ?? 'No data for this period',
            goal: text.goal ?? 'Goal',
            // msc_text is an explicit allowlist, so a key Blade passes but this omits is
            // dropped before buildChartOption() ever sees it — which is how the tooltip's
            // "Actual" row stayed English while "Goal" beside it translated.
            actual: text.actual ?? 'Actual',
        },
        // Announced politely on switch so the change is not conveyed by repaint alone.
        liveMessage: '',
        /**
         * Why the user's saved chart type was overridden, or '' when it was honoured.
         *
         * Client-owned, not server-rendered: the switch path is #[Renderless] (a re-render
         * would tear down the live canvas), so a notice printed by Blade would still be
         * claiming an override after the user picks a feasible type — contradicting the
         * picker right above it until some unrelated action forced a full re-render.
         */
        fallbackNotice: initialPayload?.fallbackReason ?? '',

        /**
         * Set when the range itself cannot carry a legible chart of any per-period shape.
         *
         * Separate from fallbackNotice, which speaks only to a dropped SAVED preference and
         * is therefore empty in the common case. This one is addressed to every viewer of
         * that range, so it drives the box instead of a footnote under it: the honest answer
         * on a grid too wide to draw is no chart, not a chart nobody can read.
         */
        chartUnavailableNotice: initialPayload?.chartUnavailableReason ?? '',

        init() {
            lifecycle = createEchartsLifecycle({
                label: 'metric chart',
                getContainer: () => this.$refs.chartContainer,
                isCurrent: (token) => token === this.chartRenderToken,
                invalidate: () => { this.chartRenderToken++; },
                rerender: () => this.msc_render(),
                onError: () => {
                    this.chartLoadFailed = true;
                    // The box swaps to the load-failed message on this path too, and the
                    // polite region is the only thing that says so to a screen reader.
                    this.liveMessage = this.msc_text.loadFailed;
                },
            });

            // After this tick, not now: the chart host inside this component starts after it,
            // and it is the host that starts listening for charts. Sent now, the first chart
            // would reach no listener and the host would wait on its skeleton for good.
            this.$nextTick(() => this.msc_render());

            this.$watch('payload', () => {
                this.selectedType = this.payload?.selectedType ?? this.selectedType;
                this.drawnType = null;

                // Announce a disappearing chart. An interval or range change with no data
                // display:none's the role="img" node and empties the table, so without this
                // a screen-reader user gets silence and no reason for it.
                // A fresh payload re-states the override (or clears it): feasibility is
                // recomputed server-side for the new interval/range.
                this.fallbackNotice = this.payload?.fallbackReason ?? '';
                this.chartUnavailableNotice = this.payload?.chartUnavailableReason ?? '';

                // This region carries only the sentences whose chart VANISHED — a genuine
                // 4.1.3 status change, and each of them accompanies a box that replaced the
                // canvas. The override sentence is deliberately NOT here: it is msc_fallbackMessage,
                // read by the visible <p>, which is itself the role="status" region. Two regions
                // holding one sentence is announced twice by NVDA and JAWS, so re-adding a
                // `this.fallbackNotice` arm below reinstates that defect.
                //
                // Both arms below are the only sites that assign their sentence, and $watch does
                // not fire on init, so this region is '' at first paint even when one of them is
                // already true then — a metric with no readings at all (SupersetChartHostProofSeeder's
                // Refunds) opens straight onto the no-data box. That is sound rather than the same
                // bug as the override's, and for one reason only: each of these sentences, and the
                // load-failed one assigned outside this watcher, is printed un-aria-hidden by the
                // box it accompanies, so this region adds an announcement rather than being the
                // sentence's only route into the accessibility tree. Seeding it would put the
                // sentence there twice.
                if (!this.msc_hasData) {
                    this.liveMessage = this.msc_text.noData;
                } else if (this.chartUnavailableNotice) {
                    // Announced on its own, not through the "Showing a different chart"
                    // template — nothing is being substituted, the chart is gone.
                    this.liveMessage = this.chartUnavailableNotice;
                } else {
                    this.liveMessage = '';
                }

                this.msc_render();
            });

            /**
             * Announce a CHANGE to the override sentence, through a region that was on the page
             * before the change and is never swapped out.
             *
             * The visible <p> cannot do this job, and measuring it with VoiceOver in Chrome is
             * what established that. A screen reader speaks a live region when text changes
             * inside a region it already knew about, and "already knew about" has requirements
             * the <p> fails on both paths that change this sentence:
             *
             * - An interval or range change leaves the <p> in place and writes into it, which is
             *   the shape that should work — and does not, in VoiceOver. Chromium's tree shows
             *   the region registered, polite and atomic, while still empty; the write is still
             *   silent. That is the region itself, not timing.
             * - A summary-method or formula change is worse and for a structural reason: the
             *   whole chart component is the @else arm of an @if in livewire/metric/view.blade.php,
             *   so Livewire INSERTS this <p> into an already-loaded page and fills it about 4ms
             *   later, before a frame is drawn. A region inserted with its text is never spoken,
             *   so no amount of aria-live on this element can fix that path.
             *
             * So the announcement goes to a dedicated sr-only region that lives OUTSIDE that @if
             * — present from first paint, empty, never removed, wire:ignore'd so no morph
             * touches it. It is the shape VoiceOver provably speaks on this very page: the only
             * region it did speak was the chart host's own sr-only aria-live span.
             *
             * It holds the sentence only while announcing and then clears, so at rest the
             * sentence is in the accessibility tree exactly once, in the visible <p>, where a
             * reader meets it in document order. The announcer is the channel; the <p> is the
             * home.
             *
             * A watcher, not an assignment inside the payload watcher: this sentence also
             * changes on the type-switch path, which is #[Renderless] and never touches the
             * payload. And $watch does not fire on init, which is exactly right here — content
             * already true at first paint is read in order from the <p> and must not also be
             * announced, or a cold load speaks a sentence the user has not reached yet.
             */
            this.$watch('msc_fallbackMessage', (message) => {
                this.$dispatch('metric-chart-fallback-notice', { message });
            });

            /**
             * The sentence can be true the moment this component STARTS, and a $watch never fires
             * for that — it only reports changes. On a cold load that is correct: the sentence is
             * content, read in document order, and announcing it would speak something the reader
             * has not reached yet.
             *
             * But this component does not only start on a cold load. Metric\View renders the chart
             * in the @else arm of an @if, so a summary-method or formula change tears it down and
             * builds it again inside a page that has long finished loading. From the user's point
             * of view that is a change they just made, and from the component's it is a first
             * paint, so nothing was announced and the path stayed silent through two review rounds.
             *
             * document.readyState tells the two apart, and it is the honest signal rather than a
             * timer: it is 'loading' while the initial document is still parsing, and 'complete'
             * by the time Livewire swaps a subtree in. No queue, no guessing at how long a load
             * takes.
             */
            if (document.readyState === 'complete' && this.msc_fallbackMessage) {
                this.$dispatch('metric-chart-fallback-notice', { message: this.msc_fallbackMessage });
            }
        },

        destroy() {
            this.msc_cleanup();
        },

        /**
         * The Superset host's draw failed. The host has already replaced its own box with its
         * sentence, its Retry button and its own live region — VoiceOver speaks that one — so
         * this records the state and announces nothing: a second message would say the chart
         * failed twice.
         *
         * What the state is for is the override footnote. Without it, "Showing a different
         * chart: ..." stayed under the host's failure box, claiming a substitution for a chart
         * that is not there.
         */
        msc_onHostFailed() {
            this.hostLoadFailed = true;
        },

        /**
         * Cleared the moment the host is asked to try again, and again when it reports a draw.
         *
         * Both, not just the draw: a retry that fails again never reports a draw, but it does
         * re-dispatch its failure, so clearing on the retry and letting the failure re-set the
         * flag keeps the footnote honest through any number of attempts. Clearing only on the
         * draw would leave the footnote suppressed while a chart was back on screen.
         */
        msc_onHostSettled() {
            this.hostLoadFailed = false;
        },

        get msc_hasData() {
            return Boolean(this.payload?.hasData && this.payload.points?.length);
        },

        /** Whether the Superset bundle draws the chart on screen, so its host's box is the one shown. */
        get msc_hostDrawn() {
            return this.drawnBy === 'host';
        },

        /**
         * The override sentence, or '' when the saved preference was honoured.
         *
         * A getter, not a property a handler assigns. This sentence can be TRUE at first
         * paint — fallbackNotice is seeded from initialPayload.fallbackReason — and $watch
         * does not fire on init, so a watcher-assigned property is empty at exactly the
         * moment the notice is already true. It used to be, which left the sentence on
         * screen for a sighted user and absent from the accessibility tree entirely. Derived,
         * it is correct at first paint by construction and needs no init() seeding.
         *
         * The visible <p> in chart.blade.php is this sentence's only home on screen and the only
         * place it rests in the accessibility tree, which is why liveMessage does not carry it.
         * Announcing a CHANGE to it is a separate job, done by the page-level announcer in
         * livewire/metric/view.blade.php (see the msc_fallbackMessage watcher in init).
         *
         * Empty while any replacement box is showing: "Showing a different chart" claims a
         * substitution, and on none of those paths is there a chart to have substituted.
         * chartUnavailableNotice preserves the x-show gate this getter replaced; chartLoadFailed
         * closes the hole that gate left for the app's own echarts — it is a client-only state,
         * so fallbackNotice stays set under "The chart could not be loaded"; hostLoadFailed
         * closes the same hole for the five shapes the SUPERSET HOST draws, which is most of
         * them. That last arm was missing: the line, bars, pie, donut and gauge are the host's,
         * including the line this override falls back to by default, so a host failure left
         * "Showing a different chart" sitting under the host's own failure box, claiming a
         * substitution for a chart that never drew.
         */
        get msc_fallbackMessage() {
            if (!this.fallbackNotice || this.chartUnavailableNotice || this.chartLoadFailed || this.hostLoadFailed) return '';

            return this.msc_fill(this.msc_text.fallback, { ':reason': this.fallbackNotice });
        },

        /**
         * Substitute :placeholders in a translated string, in ONE pass, with a function.
         *
         * Both properties are load-bearing and neither is stylistic:
         *
         * - A FUNCTION replacement is never scanned for `$$`, `$&`, `` $` `` or `$'`, which
         *   String.prototype.replace interprets as substitution patterns when the replacement
         *   is a string. A metric named `Revenue $'000s` would otherwise splice a copy of the
         *   rest of the template into its own accessible name.
         * - ONE pass means a substituted value can never be re-scanned. Filling :name first
         *   and :count second — the shape this replaced — corrupts a metric literally named
         *   "Q1 :count", because the second replace() finds the placeholder inside the name it
         *   just inserted and rewrites that instead of the real one.
         *
         * Unknown tokens collapse to '' rather than printing the raw placeholder, so a
         * translation carrying a placeholder this caller does not supply degrades to a
         * missing word instead of leaking ":count" into the UI.
         */
        msc_fill(template, values) {
            return String(template ?? '').replace(/:type|:name|:count|:reason/g, (token) => {
                const value = values[token];

                return value === undefined || value === null ? '' : String(value);
            });
        },

        /**
         * Whether the drawn chart shows ONE summary value rather than a series.
         *
         * The bullet renders payload.summary alone — a single scalar with no periods on
         * screen. The visually-hidden table and the aria-label are the accessible EQUIVALENT
         * of what is drawn, so announcing "N periods" and listing N rows for it is not merely
         * over-informative, it is a false statement about the graphic. (The gauge, the other
         * single-value shape, draws through the chart host, which describes it itself.)
         */
        get msc_isSingleValue() {
            return this.msc_drawnType === 'bullet';
        },

        /**
         * The type the canvas is actually drawing.
         *
         * Falls back to selectedType before the first paint resolves (and on the no-data
         * path, where nothing is drawn at all) so the table and the label are never blank
         * while a render is in flight.
         */
        get msc_drawnType() {
            return this.drawnType || this.selectedType;
        },

        /** The chart's accessible name, matching what the canvas actually draws. */
        get msc_ariaLabel() {
            const template = this.msc_isSingleValue
                ? this.msc_text.ariaLabelSingle
                : this.msc_text.ariaLabel;

            return this.msc_fill(template, {
                ':type': this.msc_selectedLabel,
                ':name': this.msc_text.metricName,
                ':count': (this.payload?.points || []).length,
            });
        },

        /** The visually-hidden table's caption. */
        get msc_caption() {
            return this.msc_fill(this.msc_text.caption, {
                ':type': this.msc_selectedLabel,
                ':name': this.msc_text.metricName,
            });
        },

        /**
         * The table's caption, which must not name a chart when none is drawn.
         *
         * On the range-too-wide path the table is still rendered — it is the only route to
         * these numbers for a screen-reader user — but msc_selectedLabel is '' there, so the
         * ordinary caption would read " chart data for X" and describe a canvas that is
         * provably not on screen.
         */
        get msc_tableCaption() {
            return this.chartUnavailableNotice
                ? this.msc_fill(this.msc_text.dataCaption, { ':name': this.msc_text.metricName })
                : this.msc_caption;
        },

        /** A period's row heading, marking the still-accumulating one. */
        msc_periodLabel(point) {
            const label = point?.seriesLabel || point?.axisLabel || '';

            return point?.isPartial ? `${label} (${this.msc_text.inProgress})` : label;
        },

        /**
         * The rows of the visually-hidden table, already shaped for the drawn chart.
         *
         * Derived here rather than branched in the markup so the table can never describe
         * something the canvas is not drawing: a single-value chart yields exactly one row,
         * every other type yields one row per period, padding included, as every shape the
         * app's echarts draws puts one mark (or one gap) in each period's slot.
         */
        get msc_tableRows() {
            if (this.msc_isSingleValue) {
                return [{
                    label: this.msc_text.latest,
                    value: this.payload?.summary?.formatted ?? this.msc_text.noDataCell,
                }];
            }

            return (this.payload?.points || []).map((point) => ({
                label: this.msc_periodLabel(point),
                value: this.msc_tableValue(point) ?? this.msc_text.noDataCell,
            }));
        },

        /** The label the sr-only table and the aria-label use for the current type. */
        get msc_selectedLabel() {
            const match = (this.payload?.availableTypes || []).find((t) => t.value === this.msc_drawnType);

            return match ? match.label : '';
        },

        /**
         * Whether the chart on screen shows the goal AT ALL — deliberately not "calls
         * goalMarkLine()". Two of the shapes the app's echarts draws genuinely show no goal: a
         * cumulative line and a waterfall (a per-period target means nothing against a running
         * total or against movement bars — their tooltips refuse it for the same reason).
         *
         * The visually-hidden table is the accessible EQUIVALENT of what is drawn, so citing
         * a goal the sighted reader is explicitly denied would hand screen-reader users the
         * very comparison the UI refuses to make.
         */
        get msc_showsGoal() {
            const withoutGoal = ['cumulative_line', 'waterfall'];

            return Boolean(this.payload?.goal) && !withoutGoal.includes(this.msc_drawnType);
        },

        /**
         * The value the accessible data table prints for a point.
         *
         * Must track the chart type, not just the payload: a cumulative chart plots the
         * running total while `formatted` holds the period's own value, so reading
         * `formatted` unconditionally would make the table state a different number from
         * the one drawn.
         */
        msc_tableValue(point) {
            if (this.msc_drawnType === 'cumulative_line') {
                return point?.cumulative?.formatted ?? null;
            }

            return point?.formatted ?? null;
        },

        /**
         * Switch type in the browser and persist in the background.
         *
         * The repaint is local — the payload already holds every series, every
         * formatted string, every Superset contract and the feasible list — so the chart
         * changes on the click. The server call is #[Renderless] and its result is never
         * awaited: a re-render would tear down the canvas this component just drew.
         */
        msc_select(type) {
            if (!type || type === this.selectedType) return;
            if (!(this.payload?.availableTypes || []).some((t) => t.value === type)) return;

            this.selectedType = type;
            // Cleared so msc_drawnType degrades to the newly selected type while the render
            // is in flight; msc_render() republishes it once the canvas actually paints.
            this.drawnType = null;
            // The user has just chosen a type that IS feasible, so the override notice no
            // longer describes what is on screen.
            this.fallbackNotice = '';
            this.liveMessage = this.msc_fill(this.msc_text.chartType, { ':type': this.msc_selectedLabel });
            this.msc_render();

            if (this.$wire) {
                // The result IS checked, and the rejection is real rather than theoretical:
                // the server re-resolves feasibility from fresh data, so a client payload
                // that has gone stale (a background refresh, a changed range) can name a type
                // the server now refuses. It answers false and writes nothing — and because
                // this call is #[Renderless] and the canvas has already repainted, the user
                // would otherwise see the new chart, reload, and silently get the old one
                // back with nothing having said so. A dropped request or an expired session
                // lands in the same place, plus an unhandled promise rejection.
                Promise.resolve(this.$wire.rememberChartType(type))
                    .then((saved) => {
                        if (saved === false) {
                            console.warn(
                                `[metric chart] the server refused to remember "${type}"; it will revert on reload.`
                            );
                        }
                    })
                    .catch((error) => console.error('Failed to remember the metric chart type:', error));
            }
        },

        /** Whether the Superset bundle draws this shape, by the list the payload's builder keeps. */
        msc_drawsThroughHost(type) {
            return (this.payload?.host?.types || []).includes(type);
        },

        msc_render() {
            const container = this.$refs.chartContainer;
            if (!container) return;

            // chartUnavailableNotice lands here for the same reason the no-data path does: the box
            // shows a message instead of a canvas, so drawing into a hidden container would
            // only leave a stale chart behind the next time it becomes visible.
            if (!this.msc_hasData || this.chartUnavailableNotice || !this.selectedType) {
                // Cleared here too, not only in the success path below. This early return is
                // reached whenever an interval or range change lands on an empty window, and
                // it used to leave a stale failure state: sighted users kept reading "The
                // chart could not be loaded" while the no-data box (gated on
                // !chartLoadFailed) stayed hidden, and the payload watcher simultaneously
                // announced "no data" to screen readers. The two audiences were told
                // different things and neither self-corrected until data came back.
                this.chartLoadFailed = false;
                this.drawnBy = null;
                lifecycle.clear();
                this.msc_parkHost();
                return;
            }

            if (this.msc_drawsThroughHost(this.selectedType)) {
                this.msc_drawHost(this.selectedType);
                return;
            }

            this.drawnBy = 'echarts';
            this.msc_parkHost();

            const token = ++this.chartRenderToken;

            Promise.all([
                loadECharts(),
                import('../charts/chart-options'),
            ])
                .then(([echartsModule, optionsModule]) => {
                    // A newer render (or a teardown) landed while the chunks were in flight.
                    if (token !== this.chartRenderToken) return;

                    const echarts = echartsModule.default;
                    const { buildChartOption, isDrawable } = optionsModule;

                    // An unregistered series type paints a blank canvas with no error in a
                    // production build — which is every build this repo makes — so resolve
                    // against what was actually registered rather than trusting the string.
                    // The line is the fallback, and the chart host draws it.
                    if (!isDrawable(this.selectedType, echartsModule.REGISTERED_SERIES)) {
                        console.error(
                            `[metric chart] "${this.selectedType}" has no registered echarts series; falling back to the line.`
                        );
                        this.msc_drawHost('line');
                        return;
                    }

                    // Published so the accessibility getters describe what is on the CANVAS,
                    // not what was requested.
                    this.drawnType = this.selectedType;

                    this.chartLoadFailed = false;
                    lifecycle.drawWhenBoxed(echarts, container, buildChartOption(this.selectedType, this.payload, this.msc_text), token);
                })
                .catch((error) => {
                    console.error('Failed to load echarts for the metric chart:', error);

                    if (token !== this.chartRenderToken) return;

                    this.chartLoadFailed = true;
                    this.liveMessage = this.msc_text.loadFailed;
                    this.msc_cleanup();
                });
        },

        /**
         * Hand the chart host the payload's contract for a shape Superset draws, with this
         * screen's tooltip and names. The host owns everything from there: the bundle's download,
         * the draw, its accessible name and table, and its own failure state with Retry.
         *
         * A shape the payload built no contract for cannot be drawn by anything, which only a
         * payload out of step with its own offered list could cause; it lands in the load-failed
         * box rather than a chart nobody asked for.
         */
        msc_drawHost(type) {
            const contract = this.payload?.host?.contracts?.[type];

            // Takes down the app's echarts drawing, and drops any echarts draw still loading its
            // chunks, so it cannot paint over the host's box later.
            lifecycle.clear();
            this.chartLoadFailed = false;

            if (!contract) {
                console.error(`[metric chart] the payload offers "${type}" but carries no chart for it.`);
                this.drawnBy = null;
                this.chartLoadFailed = true;
                this.liveMessage = this.msc_text.loadFailed;
                this.msc_parkHost();
                return;
            }

            this.drawnBy = 'host';
            this.drawnType = type;
            hostHolds = true;
            this.msc_sendToHost({ contract, ...this.msc_hostDetail(contract) });
        },

        /**
         * What the host is told beside the contract, per viz: the chart's title, this screen's
         * tooltip, and the words its sr-only table reads.
         *
         * - A line or bars: renderTooltipHtml() over Superset's params, one row for the reading
         *   and, where the goal line is drawn, one for the goal and the goal footer. Each row
         *   prints the server's own string for its period, found by the period's label, which is
         *   what the chart stands each period on, and the host's table calls each period what this
         *   screen calls it, the one still in progress included, and the two lines Actual and Goal.
         * - A pie or donut: the slice's value and its share, found by the slice's name, and the
         *   host's table naming each slice's period as this screen does.
         * - A gauge: no tooltip, as before. The ring prints its figure and its caption itself.
         */
        msc_hostDetail(contract) {
            const title = this.msc_text.metricName;
            const points = this.payload?.points || [];

            if (contract.vizType === 'blink_gauge') {
                return { title, tooltip: { trigger: 'item', show: false } };
            }

            if (contract.vizType === 'pie') {
                const metric = contract.formData.metric;
                const total = (contract.queriesData?.[0]?.data || [])
                    .map((row) => row[metric])
                    .filter((value) => typeof value === 'number' && value > 0)
                    .reduce((sum, value) => sum + value, 0);
                const displays = Object.fromEntries(points.filter(drawsASlice).map((point) => [point.seriesLabel, point.formatted]));
                // Each slice is a period: the host's table names it as this screen does, the one
                // still in progress included, under a Period column.
                const periods = Object.fromEntries(points.filter(drawsASlice).map((point) => [point.seriesLabel, this.msc_periodLabel(point)]));

                return {
                    title,
                    periods,
                    formatShare,
                    tooltip: {
                        ...HOST_TOOLTIP_CHROME,
                        trigger: 'item',
                        formatter: supersetSliceTooltipFormatter({ displays, total, formatShare }, { text: this.msc_text }),
                    },
                };
            }

            const { actual, goal: goalLabel } = this.payload.host.labels;
            const drawsGoal = Boolean(contract.formData.annotationLayers?.length);
            const periods = {};
            const displays = { [actual]: {}, ...(drawsGoal ? { [goalLabel]: {} } : {}) };

            // The line and bars stand on each period's own label, so the label finds its strings.
            points.forEach((point) => {
                if (typeof point.seriesLabel !== 'string') return;

                periods[point.seriesLabel] = this.msc_periodLabel(point);
                displays[actual][point.seriesLabel] = point.formatted;

                if (drawsGoal) displays[goalLabel][point.seriesLabel] = this.payload.goal?.formatted ?? null;
            });

            return {
                title,
                periods,
                seriesNames: { [actual]: this.msc_text.actual, ...(drawsGoal ? { [goalLabel]: this.msc_text.goal } : {}) },
                tooltip: {
                    ...HOST_TOOLTIP_CHROME,
                    trigger: 'axis',
                    formatter: supersetTooltipFormatter(
                        { labels: { actual, goal: drawsGoal ? goalLabel : null }, displays, goal: drawsGoal ? this.payload.host.goal : null },
                        {
                            locale: document.documentElement.lang || undefined,
                            text: this.msc_text,
                            periods,
                            horizontal: contract.formData.orientation === 'horizontal',
                        },
                    ),
                },
            };
        },

        /** The chart host inside this component, found by the id it renders on its root. */
        msc_sendToHost(detail) {
            const id = this.$root.querySelector('[data-superset-chart]')?.dataset.supersetChart;
            if (!id) return;

            window.dispatchEvent(new CustomEvent(CHART_DATA_EVENT, { detail: { id, ...detail } }));
        },

        /**
         * Take the host's chart down once another renderer (or no chart at all) takes over, so a
         * later switch back never shows the previous shape's chart for a frame. 'loading' is the
         * one status the host clears to without announcing anything.
         */
        msc_parkHost() {
            if (!hostHolds) return;

            hostHolds = false;
            this.msc_sendToHost({ status: 'loading' });
        },

        msc_cleanup() {
            lifecycle.cleanup();
        },
    };
};
